/*
Purpose: The session card rendered inside day-view slots and week-view cells.
Project: TCMS

One shell owns the lane colour, the "live" ring and the click/keyboard affordance;
three body variants render the detail level the current view asks for. The shell is a
`div` with `role="button"` rather than a `<button>` so the status and mode chips inside
it can be real buttons without nesting interactive elements.
*/

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import BoltIcon from '@mui/icons-material/Bolt';

import {
  COMPACT_AVATAR_LIMIT,
  DIAGNOSTICS_LANE_COLOR,
  DISPLAY_STATUS,
  TUTOR_LANE_COLORS,
} from '../constants';
import { initialsOf } from '../session-model';
import { Avatar, ModeBadge, StatusPill, Tag, YearChip } from './badges';

function laneColorFor(session) {
  if (session.isDiagnostics) {
    return DIAGNOSTICS_LANE_COLOR;
  }
  return TUTOR_LANE_COLORS[session.laneColorIndex % TUTOR_LANE_COLORS.length];
}

/** Title row: schedule/tutor name — not a profile link for diagnostics slots. */
function SessionTitle({ session, onTutorClick, asText }) {
  if (!session.tutorName) {
    return null;
  }

  if (session.isDiagnostics || asText) {
    return (
      <div className="cv2-card__tutor" title={session.tutorName}>
        {session.tutorName}
      </div>
    );
  }

  return (
    <div className="cv2-card__tutor">
      <button
        type="button"
        className="cv2-card__tutor-link"
        title={`Open ${session.tutorName}`}
        onClick={(event) => {
          event.stopPropagation();
          onTutorClick(session);
        }}
      >
        {session.tutorName}
      </button>
    </div>
  );
}

/** Statuses that mean nobody is attending, so the card is shown as struck out. */
const VOID_STATUSES = [
  DISPLAY_STATUS.CANCELLED,
  DISPLAY_STATUS.FORFEIT,
  DISPLAY_STATUS.WRITEOFF,
];

/** Statuses already named by the status pill — no extra One-off / Deferred tag. */
const WORKFLOW_STATUSES = [
  DISPLAY_STATUS.DEFER_REQUESTED,
  DISPLAY_STATUS.SLOT_ASSIGN,
  DISPLAY_STATUS.ONE_OFF_REQUESTED,
];

/**
 * Placement marker on a student row.
 * Cancelled / forfeit / writeoff / open request → status only.
 * One-off matches production (`reassigned`); Deferred only when explicitly defer-sourced.
 */
function placementTag(session, student) {
  if (session.isDiagnostics) {
    return null;
  }
  if (VOID_STATUSES.includes(student.status) || WORKFLOW_STATUSES.includes(student.status)) {
    return null;
  }
  if (student.isOneOff || session.isOneOffClass) {
    return { variant: 'oneoff', label: 'One-off' };
  }
  if (student.isDeferred) {
    return { variant: 'deferred', label: 'Deferred' };
  }
  return null;
}

function emptyStudentsLabel(session) {
  return session.isDiagnostics ? 'No students in this slot' : 'No students enrolled';
}

function isSessionVoid(session) {
  return (
    session.students.length > 0 &&
    session.students.every((student) => VOID_STATUSES.includes(student.status))
  );
}

function previewStyleFromAnchor(anchorRect) {
  const width = 260;
  const gap = 10;
  const margin = 10;
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const minNeeded = 160;

  const spaceRight = vw - anchorRect.right - gap - margin;
  const spaceLeft = anchorRect.left - gap - margin;
  const spaceBelow = vh - anchorRect.bottom - gap - margin;
  const spaceAbove = anchorRect.top - gap - margin;

  // Prefer beside the card (right, then left) so the preview does not cover the compact card.
  if (spaceRight >= width) {
    const top = Math.min(
      Math.max(margin, anchorRect.top),
      Math.max(margin, vh - margin - minNeeded)
    );
    return {
      left: anchorRect.right + gap,
      top,
      width,
      maxHeight: Math.max(minNeeded, vh - top - margin),
    };
  }

  if (spaceLeft >= width) {
    const top = Math.min(
      Math.max(margin, anchorRect.top),
      Math.max(margin, vh - margin - minNeeded)
    );
    return {
      left: anchorRect.left - gap - width,
      top,
      width,
      maxHeight: Math.max(minNeeded, vh - top - margin),
    };
  }

  const left = Math.min(
    Math.max(margin, anchorRect.left),
    Math.max(margin, vw - width - margin)
  );

  if (spaceBelow >= minNeeded || spaceBelow >= spaceAbove) {
    const top = anchorRect.bottom + gap;
    return {
      left,
      top,
      width,
      maxHeight: Math.max(minNeeded, Math.min(360, spaceBelow)),
    };
  }

  return {
    left,
    bottom: vh - anchorRect.top + gap,
    width,
    maxHeight: Math.max(minNeeded, Math.min(360, spaceAbove)),
  };
}

/** Hover preview for compact cards — same layout/colors as the detailed session card. */
function CompactHoverPreview({ session, anchorRect, laneColor }) {
  if (!anchorRect) {
    return null;
  }

  const portalRoot = document.querySelector('.cv2-root') || document.body;
  const subjects = Array.from(
    new Set(session.students.map((student) => student.subjectName).filter(Boolean))
  );
  const subjectLabel = subjects.join(', ');

  return createPortal(
    <div
      className={`cv2-card cv2-card-preview${session.isDiagnostics ? ' cv2-card--diagnostics' : ''}`}
      style={{
        ...previewStyleFromAnchor(anchorRect),
        background: laneColor.surface,
        borderColor: laneColor.edge,
      }}
      role="tooltip"
    >
      <SessionTitle session={session} onTutorClick={() => {}} asText />

      <div className="cv2-card__head">
        <span className="cv2-card__time">
          {session.timeRangeLabel || session.startTimeLabel}
        </span>
        <span className="cv2-card__lessons">
          {session.lessonCount} student{session.lessonCount === 1 ? '' : 's'}
        </span>
        {session.hasFlexiStudent ? <BoltIcon className="cv2-icon" titleAccess="Flexi" /> : null}
      </div>

      {session.isDiagnostics ? (
        <div className="cv2-card__subject">Initial Diagnostics</div>
      ) : subjectLabel ? (
        <div className="cv2-card__subject" title={subjectLabel}>
          {subjectLabel}
        </div>
      ) : null}

      <div className="cv2-card__students">
        {session.students.length === 0 ? (
          <div className="cv2-card__empty-students">{emptyStudentsLabel(session)}</div>
        ) : (
          session.students.map((student) => (
            <div key={student.key}>
              <div className="cv2-student-row">
                {session.isDiagnostics ? null : (
                  <ModeBadge mode={student.deliveryMode} showLabel={false} />
                )}
                <div>
                  <div className="d-flex align-items-center gap-1">
                    <span
                      className={`cv2-student-row__name${
                        student.isStruckThrough ? ' cv2-student-row__name--struck' : ''
                      }`}
                      title={student.name}
                    >
                      {student.isReassigned ? '*' : ''}
                      {student.name}
                    </span>
                    {session.isDiagnostics ? null : <YearChip yearLevel={student.yearLevel} />}
                  </div>
                  {student.subjectName ? (
                    <div className="cv2-student-row__subject">{student.subjectName}</div>
                  ) : null}
                  <div className="cv2-student-row__meta">
                    <StatusPill
                      status={student.isPaused ? DISPLAY_STATUS.PAUSED : student.status}
                      showDot
                    />
                    {placementTag(session, student) ? (
                      <Tag variant={placementTag(session, student).variant}>
                        {placementTag(session, student).label}
                      </Tag>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    portalRoot
  );
}

/** Detailed body: every student listed with their own mode and status chips. */
function DetailedBody({ session, onStatusClick, onModeClick, onStudentClick, onTutorClick }) {
  const subjects = Array.from(
    new Set(session.students.map((student) => student.subjectName).filter(Boolean))
  );
  const subjectLabel = subjects.join(', ');
  const hasMarkers =
    !session.isDiagnostics &&
    (session.deferredCount > 0 ||
      session.oneOffCount > 0 ||
      session.makeUpCount > 0 ||
      session.isOneOffClass ||
      session.isMakeUpClass);

  return (
    <>
      <SessionTitle session={session} onTutorClick={onTutorClick} />

      <div className="cv2-card__head">
        <span className="cv2-card__time">{session.timeRangeLabel}</span>
        <span className="cv2-card__lessons">
          {session.lessonCount} student{session.lessonCount === 1 ? '' : 's'}
        </span>
      </div>

      {session.isDiagnostics ? (
        <div className="cv2-card__subject">Initial Diagnostics</div>
      ) : subjectLabel ? (
        <div className="cv2-card__subject" title={subjectLabel}>
          {subjectLabel}
        </div>
      ) : null}

      {hasMarkers ? (
        <div className="cv2-card__markers">
          {session.deferredCount > 0 ? (
            <Tag variant="deferred">
              {session.deferredCount} Deferred
            </Tag>
          ) : null}
          {session.oneOffCount > 0 || session.isOneOffClass ? (
            <Tag variant="oneoff">
              {session.oneOffCount > 0 ? `${session.oneOffCount} One-off` : 'One-off'}
            </Tag>
          ) : null}
          {session.makeUpCount > 0 || session.isMakeUpClass ? (
            <Tag variant="makeup">
              {session.makeUpCount > 0 ? `${session.makeUpCount} Make-up` : 'Make-up'}
            </Tag>
          ) : null}
        </div>
      ) : null}

      <div className="cv2-card__students">
        {session.students.length === 0 ? (
          <div className="cv2-card__empty-students">{emptyStudentsLabel(session)}</div>
        ) : (
          session.students.map((student) => (
            <div key={student.key}>
              <div className="cv2-student-row">
                {session.isDiagnostics ? null : (
                  <ModeBadge
                    mode={student.deliveryMode}
                    showLabel={false}
                    onClick={(event) => {
                      event.stopPropagation();
                      onModeClick(session, student);
                    }}
                  />
                )}
                <div>
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className={`cv2-student-row__name cv2-student-row__name--link${
                        student.isStruckThrough ? ' cv2-student-row__name--struck' : ''
                      }`}
                      title={student.name}
                      onClick={(event) => {
                        event.stopPropagation();
                        onStudentClick(student);
                      }}
                    >
                      {student.isReassigned ? '*' : ''}
                      {student.name}
                    </button>
                    {session.isDiagnostics ? null : <YearChip yearLevel={student.yearLevel} />}
                  </div>
                  {student.subjectName ? (
                    <div className="cv2-student-row__subject">{student.subjectName}</div>
                  ) : null}
                  <div className="cv2-student-row__meta">
                    <StatusPill
                      status={student.isPaused ? DISPLAY_STATUS.PAUSED : student.status}
                      showDot
                      onClick={
                        student.isAttendanceEditable
                          ? (event) => {
                              event.stopPropagation();
                              onStatusClick(session, student);
                            }
                          : undefined
                      }
                    />
                    {placementTag(session, student) ? (
                      <Tag variant={placementTag(session, student).variant}>
                        {placementTag(session, student).label}
                      </Tag>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/** Week compact chip: tutor initials + student count, solid lane colour. */
function WeekChipBody({ session, tutorInitials }) {
  const initials = tutorInitials || initialsOf(session.tutorName);
  const count = session.lessonCount || 0;
  const title = session.isDiagnostics
    ? `${session.tutorName || 'Initial Diagnostics'} (${count})`
    : session.tutorName || initials;

  return (
    <div className="cv2-week-chip" title={title}>
      <span className="cv2-week-chip__initials">{session.isDiagnostics ? 'ID' : initials || '—'}</span>
      <span className="cv2-week-chip__count">{count}</span>
    </div>
  );
}

/** Compact body: an avatar pile plus aggregate counts, matching the density toggle. */
function CompactBody({ session, showTutorName, onTutorClick }) {
  const shown = session.students.slice(0, COMPACT_AVATAR_LIMIT);
  const overflow = session.students.length - shown.length;
  const subjects = Array.from(
    new Set(session.students.map((student) => student.subjectName).filter(Boolean))
  );
  const subjectLabel = session.isDiagnostics
    ? 'Initial Diagnostics'
    : subjects.length === 0
      ? ''
      : subjects.length === 1
        ? subjects[0]
        : `${subjects.length} subjects`;

  return (
    <div className="cv2-compact">
      {showTutorName ? <SessionTitle session={session} onTutorClick={onTutorClick} /> : null}

      <div className="cv2-compact__row">
        <div className="cv2-compact__time">
          {session.startTimeLabel}
          {session.durationLabel ? ` · ${session.durationLabel}` : ''}
        </div>
      </div>
      <div className="cv2-compact__row">
        <div className="cv2-compact__type">
          {subjectLabel || 'No subject'} ·{' '}
          <strong>
            {session.lessonCount} student{session.lessonCount === 1 ? '' : 's'}
          </strong>
        </div>
      </div>

      <div className="cv2-compact__row">
        <div className="cv2-compact__pile">
          {shown.map((student) => (
            <Avatar
              key={student.key}
              size="sm"
              initials={student.initials}
              color={student.avatarColor}
              title={student.name}
            />
          ))}
          {overflow > 0 ? <span className="cv2-compact__overflow">+{overflow}</span> : null}
        </div>

        {session.isLive ? (
          <span className="cv2-compact__live">
            <span className="cv2-live-dot" />
            {session.ongoingCount} of {session.lessonCount} active
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Week detailed body: start time, tutor name and the full student list (no +N). */
function WeekBody({ session, tutorInitials, onStudentClick, onTutorClick, onStatusClick }) {
  const subjects = Array.from(
    new Set(session.students.map((student) => student.subjectName).filter(Boolean))
  );

  return (
    <>
      <SessionTitle
        session={
          session.tutorName
            ? session
            : { ...session, tutorName: tutorInitials || '' }
        }
        onTutorClick={onTutorClick}
      />
      <div className="cv2-card__head">
        <span className="cv2-card__time">{session.timeRangeLabel || session.startTimeLabel}</span>
      </div>
      {session.isDiagnostics ? (
        <div className="cv2-card__subject">Initial Diagnostics</div>
      ) : subjects.length > 0 ? (
        <div className="cv2-card__subject" title={subjects.join(', ')}>
          {subjects.join(', ')}
        </div>
      ) : null}

      <div className="cv2-card__students">
        {session.students.length === 0 ? (
          <div className="cv2-card__empty-students">
            {session.isDiagnostics ? 'No students in this slot' : 'No students'}
          </div>
        ) : (
          session.students.map((student) => (
            <div className="cv2-student-row" key={student.key}>
              {session.isDiagnostics ? null : (
                <ModeBadge mode={student.deliveryMode} showLabel={false} />
              )}
              <div className="cv2-student-row__body">
                <div className="cv2-student-row__line">
                  <button
                    type="button"
                    className={`cv2-student-row__name cv2-student-row__name--link${
                      student.isStruckThrough ? ' cv2-student-row__name--struck' : ''
                    }`}
                    title={student.name}
                    onClick={(event) => {
                      event.stopPropagation();
                      onStudentClick(student);
                    }}
                  >
                    {student.isReassigned ? '*' : ''}
                    {student.name}
                  </button>
                  {session.isDiagnostics ? null : <YearChip yearLevel={student.yearLevel} />}
                </div>
                {student.subjectName ? (
                  <div className="cv2-student-row__subject">{student.subjectName}</div>
                ) : null}
                <div className="cv2-student-row__meta">
                  <StatusPill
                    status={student.isPaused ? DISPLAY_STATUS.PAUSED : student.status}
                    showDot
                    onClick={
                      student.isAttendanceEditable
                        ? (event) => {
                            event.stopPropagation();
                            onStatusClick(session, student);
                          }
                        : undefined
                    }
                  />
                  {placementTag(session, student) ? (
                    <Tag variant={placementTag(session, student).variant}>
                      {placementTag(session, student).label}
                    </Tag>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.session Session view model from `buildSession`.
 * @param {'detailed'|'compact'|'week'|'weekChip'} props.variant Which body to render.
 * @param {Function} props.onOpen Called with the session when the card is activated.
 * @param {Function} props.onStatusClick Called with `(session, student)`.
 * @param {Function} props.onModeClick Called with `(session, student)`.
 */
export default function SessionCard({
  session,
  variant,
  onOpen,
  onStatusClick,
  onModeClick,
  onStudentClick,
  onTutorClick,
  tutorInitials,
  showTutorName,
}) {
  const cardRef = useRef(null);
  const [hoverRect, setHoverRect] = useState(null);
  const laneColor = laneColorFor(session);
  const isVoid = isSessionVoid(session);
  const isChip = variant === 'weekChip';
  const showCompactPreview = (variant === 'compact' || isChip) && hoverRect;

  const className = [
    'cv2-card',
    isChip ? 'cv2-card--week-chip' : '',
    session.isDiagnostics ? 'cv2-card--diagnostics' : '',
    session.isLive ? 'cv2-card--live' : '',
    isVoid ? 'cv2-card--void' : '',
  ]
    .filter(Boolean)
    .join(' ');

  function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(session);
    }
  }

  function showPreview() {
    if ((variant !== 'compact' && variant !== 'weekChip') || !cardRef.current) {
      return;
    }
    setHoverRect(cardRef.current.getBoundingClientRect());
  }

  function hidePreview() {
    setHoverRect(null);
  }

  useEffect(() => {
    if (!hoverRect) {
      return undefined;
    }
    function dismiss() {
      setHoverRect(null);
    }
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [hoverRect]);

  const chipStyle = isChip
    ? {
        background: laneColor.surface,
        borderColor: laneColor.edge,
        color: laneColor.ink,
      }
    : {
        background: laneColor.surface,
        borderColor: laneColor.edge,
      };

  return (
    <>
      <div
        ref={cardRef}
        className={className}
        style={chipStyle}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(session)}
        onKeyDown={handleKeyDown}
        onMouseEnter={variant === 'compact' || isChip ? showPreview : undefined}
        onMouseLeave={variant === 'compact' || isChip ? hidePreview : undefined}
        aria-label={`${session.tutorName} ${session.timeRangeLabel}, ${session.lessonCount} students, ${session.statusLabel}`}
      >
        {variant === 'compact' ? (
          <CompactBody
            session={session}
            showTutorName={showTutorName}
            onTutorClick={onTutorClick}
          />
        ) : null}
        {variant === 'weekChip' ? (
          <WeekChipBody session={session} tutorInitials={tutorInitials} />
        ) : null}
        {variant === 'detailed' ? (
          <DetailedBody
            session={session}
            onStatusClick={onStatusClick}
            onModeClick={onModeClick}
            onStudentClick={onStudentClick}
            onTutorClick={onTutorClick}
          />
        ) : null}
        {variant === 'week' ? (
          <WeekBody
            session={session}
            tutorInitials={tutorInitials}
            onStudentClick={onStudentClick}
            onTutorClick={onTutorClick}
            onStatusClick={onStatusClick}
          />
        ) : null}
      </div>
      {showCompactPreview ? (
        <CompactHoverPreview
          session={session}
          anchorRect={hoverRect}
          laneColor={laneColor}
        />
      ) : null}
    </>
  );
}

const sessionShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  students: PropTypes.array.isRequired,
  status: PropTypes.string.isRequired,
  lessonCount: PropTypes.number.isRequired,
  laneColorIndex: PropTypes.number.isRequired,
});

SessionCard.propTypes = {
  session: sessionShape.isRequired,
  variant: PropTypes.oneOf(['detailed', 'compact', 'week', 'weekChip']).isRequired,
  onOpen: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func,
  onModeClick: PropTypes.func,
  onStudentClick: PropTypes.func,
  onTutorClick: PropTypes.func,
  tutorInitials: PropTypes.string,
  showTutorName: PropTypes.bool,
};

SessionCard.defaultProps = {
  onStatusClick: () => {},
  onModeClick: () => {},
  onStudentClick: () => {},
  onTutorClick: () => {},
  tutorInitials: '',
  showTutorName: false,
};

DetailedBody.propTypes = {
  session: sessionShape.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onModeClick: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
};

CompactBody.propTypes = {
  session: sessionShape.isRequired,
  showTutorName: PropTypes.bool,
  onTutorClick: PropTypes.func.isRequired,
};

CompactBody.defaultProps = {
  showTutorName: false,
};

WeekChipBody.propTypes = {
  session: sessionShape.isRequired,
  tutorInitials: PropTypes.string,
};

WeekChipBody.defaultProps = {
  tutorInitials: '',
};

WeekBody.propTypes = {
  session: sessionShape.isRequired,
  tutorInitials: PropTypes.string,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
};

WeekBody.defaultProps = {
  tutorInitials: '',
};

CompactHoverPreview.propTypes = {
  session: sessionShape.isRequired,
  anchorRect: PropTypes.shape({
    top: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    right: PropTypes.number.isRequired,
  }).isRequired,
  laneColor: PropTypes.shape({
    surface: PropTypes.string.isRequired,
    edge: PropTypes.string.isRequired,
  }).isRequired,
};
